import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  StarIcon, BookOpenIcon, ChartLineUpIcon, BellRingingIcon, UsersThreeIcon, TrophyIcon,
  WalletIcon, TrendUpIcon, RocketLaunchIcon, SpeakerHighIcon, SpeakerSlashIcon, AddressBookIcon,
  BinocularsIcon, TargetIcon, CurrencyDollarIcon, KeyIcon, DetectiveIcon, PlantIcon, PulseIcon,
  ListIcon, XIcon, CaretLeftIcon, CaretRightIcon, UserCircleIcon, SignOutIcon, ShieldCheckIcon, NewspaperIcon, XLogoIcon, SealCheckIcon, FireIcon, HandshakeIcon, FlameIcon, FlagBannerIcon, GithubLogoIcon, SunHorizonIcon, TelegramLogoIcon, GraphIcon, SparkleIcon, CrosshairIcon, FunnelIcon, HourglassIcon,
} from "@phosphor-icons/react";
import { useFreshDropBadge } from "./FreshDropWatcher";
import { isSoundOn, setSoundOn, playChime } from "../lib/alphaSound";
import { useCreatorAlerts } from "../lib/useCreatorAlerts";
import { useClusterAlerts } from "../lib/useClusterAlerts";
import { useCabalAlerts } from "../lib/useCabalAlerts";
import { useHandleAlerts } from "../lib/useHandleAlerts";
import { useCandleAlerts } from "../lib/useCandleAlerts";
import { useRobinhoodAlerts } from "../lib/useRobinhoodAlerts";
import { useAuth } from "../context/AuthContext";
import { useChain } from "../context/ChainContext";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";

const NAV = [
  { to: "/", label: "Radar", icon: ChartLineUpIcon, id: "nav-radar", desc: "Live coins ranked by Degen Score — your main hunting board." },
  { to: "/morning-call", label: "Morning Call", icon: SunHorizonIcon, id: "nav-morning-call", hot: true, desc: "What mattered while you slept — the night's best setups across every radar, ranked, with the one to look at." },
  { to: "/trending", label: "Trending", icon: FireIcon, id: "nav-trending", hot: true, desc: "The day's hot narratives — meme themes clustering on-chain + what's trending worldwide, and the tokens riding it." },
  { to: "/launchpad", label: "Launchpad", icon: RocketLaunchIcon, id: "nav-launchpad", desc: "Pump.fun coins nearing migration, by bonding-curve % + buy velocity." },
  { to: "/smart-money", label: "Smart Money", icon: UsersThreeIcon, id: "nav-smart-money", desc: "Track proven wallets and see their buys & sells live." },
  { to: "/clusters", label: "Cluster Buys", icon: TargetIcon, id: "nav-clusters", desc: "When several tracked wallets pile into the same coin at once." },
  { to: "/cabal", label: "Cabal Radar", icon: DetectiveIcon, id: "nav-cabal", desc: "Groups of wallets that move together — insider-style clusters." },
  { to: "/cto", label: "CTO Radar", icon: HandshakeIcon, id: "nav-cto", desc: "Community Take Overs — tokens the dev abandoned and the community is now running." },
  { to: "/milly", label: "Milly's Call", icon: CurrencyDollarIcon, id: "nav-milly", desc: "The biggest single & stacking SOL buys from your wallets." },
  { to: "/organic-runners", label: "Organic Runners", icon: PlantIcon, id: "nav-organic-runners", desc: "Survivor-then-run organic movers — no paid boosts." },
  { to: "/candle-radar", label: "Candle Radar", icon: FlameIcon, id: "nav-candle-radar", desc: "Catch tokens by the candle they're forming — 🕯️ Launch Candle (h6=h24) & 📈 Carry (still rising)." },
  { to: "/momentum-radar", label: "Momentum Radar", icon: PulseIcon, id: "nav-momentum-radar", desc: "Multi-timeframe states — reads m5/h1 vs h6/h24 to flag continuation setups, breakouts, coiling & fading. Search any token." },
  { to: "/funding-web", label: "Funding Web", icon: GraphIcon, id: "nav-funding-web", desc: "Map of deployers bankrolled by the same wallet — spot token farms running many 'different' devs from one money source." },
  { to: "/survivor-watch", label: "Survivor Watch", icon: TargetIcon, id: "nav-survivor-watch", desc: "Quality survivors tracked before they pop — auto-added + your paste-ins, pinged on breakout." },
  { to: "/robinhood-feed", label: "Robinhood Feed", icon: FlagBannerIcon, id: "nav-robinhood-feed", desc: "Live Robinhood Chain tokens matching your keywords (truth/trump/djt), ranked by holders so the real one stands out." },
  { to: "/watchlist-runners", label: "Watchlist Runners", icon: PulseIcon, id: "nav-watchlist-runners", desc: "Coins on your watchlist that are starting to run." },
  { to: "/wallet-buys", label: "Wallet Buys", icon: TrendUpIcon, id: "nav-wallet-buys", desc: "Every token your tracked wallets are buying, deduped." },
  { to: "/devs", label: "Creators", icon: AddressBookIcon, id: "nav-devs", desc: "Your Alpha devs to trust and Blocked devs to avoid." },
  { to: "/launch-hub", label: "Launch Hub", icon: BinocularsIcon, id: "nav-creator-coins", desc: "Every launch from your saved devs — Creators, Proven Devs & My Builders — one newest-first feed with multi-timeframe context." },
  { to: "/fresh-drops", label: "Fresh Drops", icon: SparkleIcon, id: "nav-fresh-drops", desc: "Brand-new launches (72h) from your saved devs — who launched it, their 🎯 hit rate, live stats and one-tap buy. Fed by an on-chain sweeper so nothing slips past." },
  { to: "/wallet-scout", label: "Wallet Scout", icon: CrosshairIcon, id: "nav-wallet-scout", desc: "Paste any CA to rip its top holders, plus the Insider Radar — wallets that keep showing up early in bonding-curve graduates. One-tap track." },
  { to: "/screener", label: "Screener", icon: FunnelIcon, id: "nav-screener", desc: "Filter the live Solana universe by market cap, volume, liquidity and age ranges — save your sweet-spot presets." },
  { to: "/waiting-room", label: "Waiting Room", icon: HourglassIcon, id: "nav-waiting-room", desc: "Quiet 10-50K mcap coins with real token info that haven't run yet — re-checked every 5 min, Telegram ping + auto-watchlist the moment one wakes up." },
  { to: "/confirmation", label: "Confirmation", icon: CrosshairIcon, id: "nav-confirmation", desc: "Tier 3 overlay: young coins already showing real confirmation — degen/conviction building early plus KOLs posting the CA. Strong hits chime in-app and ping Telegram." },
  { to: "/handle-radar", label: "Handle Radar", icon: XLogoIcon, id: "nav-handle-radar", desc: "Watch X handles — catch tokens that claim them, with a verify caveat." },
  { to: "/github-radar", label: "GitHub Radar", icon: GithubLogoIcon, id: "nav-github-radar", desc: "Is the dev actually building? Checks each token's linked GitHub for real, recent commits — a legitimacy signal no other terminal has." },
  { to: "/signal-scanner", label: "Signal Scanner", icon: TelegramLogoIcon, id: "nav-signal-scanner", desc: "Watch public Telegram alpha channels — get flagged when a coin is called early, tagged with the exact channel." },
  { to: "/leaderboard", label: "Leaderboard", icon: TrophyIcon, id: "nav-leaderboard", desc: "Top smart-money wallets ranked by PnL & win-rate." },
  { to: "/my-builders", label: "My Builders", icon: SealCheckIcon, id: "nav-my-builders", desc: "Deployers you follow, with track records — their launches ping you." },
  { to: "/proven-devs", label: "Proven Devs", icon: TrophyIcon, id: "nav-proven-devs", desc: "Free leaderboard of the best deployers by runners, peak mcap & hit-rate — one-tap add to Alpha." },
  { to: "/watchlist", label: "Watchlist", icon: StarIcon, id: "nav-watchlist", desc: "Coins you've saved to keep an eye on." },
  { to: "/my-wallet", label: "My Wallet", icon: WalletIcon, id: "nav-my-wallet", desc: "Paste your holdings to track live value & PnL." },
  { to: "/alerts", label: "Alerts", icon: BellRingingIcon, id: "nav-alerts", desc: "Set up Telegram pings for the signals you care about." },
  { to: "/methodology", label: "Methodology", icon: BookOpenIcon, id: "nav-methodology", desc: "How the Degen Score and every engine is calculated." },
  { to: "/key", label: "Key", icon: KeyIcon, id: "nav-key", desc: "Plain-English glossary of every badge & signal." },
];

const badgeTestId = (id) =>
  id === "nav-creator-coins" ? "creator-coins-badge" : id === "nav-cabal" ? "cabal-badge" : id === "nav-handle-radar" ? "handle-radar-badge" : id === "nav-candle-radar" ? "candle-radar-badge" : id === "nav-robinhood-feed" ? "robinhood-feed-badge" : id === "nav-fresh-drops" ? "fresh-drops-badge" : "clusters-badge";

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { pathname } = useLocation();
  const [sound, setSound] = useState(isSoundOn());
  const [mobileOpen, setMobileOpen] = useState(false);
  const { newCount } = useCreatorAlerts();
  const { newCount: clusterNewCount } = useClusterAlerts();
  const { newCount: cabalNewCount } = useCabalAlerts();
  const { newCount: handleNewCount } = useHandleAlerts();
  const { newCount: candleNewCount } = useCandleAlerts();
  const freshDropCount = useFreshDropBadge();
  const { newCount: robinhoodNewCount } = useRobinhoodAlerts();
  const { user, isAuthed, openAuth, logout } = useAuth();
  const { meta } = useChain();

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    setSoundOn(next);
    if (next) playChime();
  };

  const badgeFor = (id) =>
    id === "nav-creator-coins" ? newCount : id === "nav-clusters" ? clusterNewCount : id === "nav-cabal" ? cabalNewCount : id === "nav-handle-radar" ? handleNewCount : id === "nav-candle-radar" ? candleNewCount : id === "nav-robinhood-feed" ? robinhoodNewCount : id === "nav-fresh-drops" ? freshDropCount : 0;

  const NavItem = ({ n }) => {
    const active = pathname === n.to;
    const Icon = n.icon;
    const badge = !active ? badgeFor(n.id) : 0;
    const link = (
      <Link
        to={n.to}
        data-testid={n.id}
        className={`relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150 ${
          active
            ? "bg-neon/10 border-l-neon text-neon"
            : n.hot
            ? "border-l-transparent text-[#FF7A1A] hover:text-[#FF9A4D] hover:bg-[#FF7A1A]/5"
            : "border-l-transparent text-muted-foreground hover:text-white hover:bg-white/5"
        } ${collapsed ? "lg:justify-center lg:px-0" : ""}`}
      >
        <Icon size={20} weight={active || n.hot ? "fill" : "regular"} className={`shrink-0 ${n.hot && !active ? "animate-pulse" : ""}`} />
        <span className={collapsed ? "lg:hidden" : ""}>{n.label}</span>
        {badge > 0 && (
          <span
            data-testid={badgeTestId(n.id)}
            className={`min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#F0C000] text-black font-mono font-bold text-[10px] leading-none animate-pulse ${
              collapsed ? "lg:absolute lg:top-1 lg:right-1" : "ml-auto"
            }`}
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </Link>
    );
    if (!n.desc) return link;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="max-w-[220px] bg-[#0b0d10] border border-white/15 text-white p-2.5 z-[60]">
          <div className="font-heading font-bold text-[11px] mb-0.5 tracking-tight">{n.label}</div>
          <div className="text-[11px] leading-relaxed text-muted-foreground">{n.desc}</div>
        </TooltipContent>
      </Tooltip>
    );
  };

  const panel = (
    <>
      {/* Brand */}
      <div className={`flex items-center gap-2.5 px-3 h-16 shrink-0 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}>
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group min-w-0">
          <img src="/logo.png" alt="AlphaApeing" className="w-9 h-9 object-contain transition-transform duration-150 group-hover:scale-110 shrink-0" />
          <div className={`leading-none min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
            <div className="font-heading font-black text-lg tracking-tighter truncate">ALPHAAPEING</div>
            <div data-testid="header-chain-tag" className="font-mono text-[10px] tracking-widest truncate" style={{ color: meta.color }}>
              {meta.short} · DEGEN SCORE
            </div>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 overflow-y-auto no-scrollbar py-2 flex flex-col gap-0.5">
        {NAV.map((n) => <NavItem key={n.to} n={n} />)}
        {user?.role === "admin" && (
          <>
            <NavItem n={{ to: "/newsroom", label: "Newsroom", icon: NewspaperIcon, id: "nav-newsroom", desc: "Private — AlphaApeing's data-first takes, ready to post to X." }} />
            <NavItem n={{ to: "/admin", label: "Admin", icon: ShieldCheckIcon, id: "nav-admin", desc: "Site stats, users, media & moderation." }} />
          </>
        )}
      </nav>

      {/* Footer controls */}
      <div className="shrink-0 border-t border-white/10 p-2.5 space-y-2">
        {isAuthed ? (
          <div className={`flex items-center gap-2 ${collapsed ? "lg:flex-col" : ""}`}>
            <span data-testid="user-chip" title={user.email} className={`flex items-center gap-1.5 px-2.5 py-2 border border-neon/30 text-neon text-xs font-mono min-w-0 flex-1 ${collapsed ? "lg:hidden" : ""}`}>
              <UserCircleIcon size={16} weight="fill" className="shrink-0" /> <span className="truncate">{user.email.split("@")[0]}</span>
            </span>
            <button data-testid="logout-btn" onClick={logout} title="Log out" className="flex items-center justify-center px-2.5 py-2 border border-transparent text-muted-foreground hover:text-danger transition-colors shrink-0">
              <SignOutIcon size={18} />
            </button>
          </div>
        ) : (
          <button data-testid="header-signin-btn" onClick={() => openAuth("login")} className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors`}>
            <UserCircleIcon size={16} weight="fill" /> <span className={collapsed ? "lg:hidden" : ""}>Sign in</span>
          </button>
        )}
        <div className={`flex items-center gap-2 ${collapsed ? "lg:flex-col" : ""}`}>
          <button
            data-testid="sound-toggle"
            onClick={toggleSound}
            title={sound ? "Alpha chime on" : "Alpha chime off"}
            className={`flex items-center justify-center gap-2 px-2.5 py-2 border transition-colors flex-1 ${sound ? "text-[#F0C000] border-[#C9A227]/40" : "text-muted-foreground border-white/10 hover:text-white"}`}
          >
            {sound ? <SpeakerHighIcon size={18} weight="fill" /> : <SpeakerSlashIcon size={18} />}
            <span className={`text-xs ${collapsed ? "lg:hidden" : ""}`}>{sound ? "Sound on" : "Sound off"}</span>
          </button>
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className="hidden lg:flex items-center justify-center px-2.5 py-2 border border-white/10 text-muted-foreground hover:text-white transition-colors shrink-0"
          >
            {collapsed ? <CaretRightIcon size={18} weight="bold" /> : <CaretLeftIcon size={18} weight="bold" />}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div data-testid="mobile-topbar" className="lg:hidden sticky top-0 z-40 flex items-center gap-3 h-14 px-3 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu" className="p-2 text-white hover:text-neon transition-colors">
          <ListIcon size={24} weight="bold" />
        </button>
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img src="/logo.png" alt="AlphaApeing" className="w-8 h-8 object-contain shrink-0" />
          <span className="font-heading font-black text-base tracking-tighter truncate">ALPHAAPEING</span>
        </Link>
        <div className="flex-1" />
        {!isAuthed && (
          <button data-testid="mobile-signin-btn" onClick={() => openAuth("login")} className="flex items-center gap-1.5 px-3 py-1.5 bg-neon text-black font-semibold text-sm">
            <UserCircleIcon size={16} weight="fill" /> Sign in
          </button>
        )}
      </div>

      {/* Desktop sidebar (fixed) */}
      <aside
        data-testid="app-sidebar"
        className={`hidden lg:flex fixed inset-y-0 left-0 z-50 flex-col bg-[#07090c]/95 backdrop-blur-xl border-r border-white/10 transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-60"}`}
      >
        {panel}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[70]" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[80%] max-w-[300px] bg-[#07090c] border-r border-white/10 flex flex-col animate-in slide-in-from-left duration-200">
            <button data-testid="mobile-drawer-close" onClick={() => setMobileOpen(false)} aria-label="Close menu" className="absolute top-4 right-3 z-10 p-1.5 text-muted-foreground hover:text-white">
              <XIcon size={22} weight="bold" />
            </button>
            {panel}
          </div>
        </div>
      )}
    </>
  );
};
